import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchStylePropertiesList } from '@/api/my-styles';
import {
  useNetworkCompanyOptions,
  useStyleTypeOptions,
} from '@/hooks/useDropdowns';
import { useSessionStore } from '@/store/sessionStore';

export type StylePropertyCategory =
  | 'brands'
  | 'departments'
  | 'seasons'
  | 'collections';

export function stylePropertyCategoryQueryOptions(
  category: StylePropertyCategory,
  companyId: string,
) {
  return queryOptions({
    queryKey: ['options', 'style-properties', category, companyId] as const,
    queryFn: async () => {
      const LIMIT = 200;
      const first = await fetchStylePropertiesList({
        category,
        companyId,
        page: 1,
        limit: LIMIT,
      });
      const totalPages = first?.metadata?.pagination?.totalPages ?? 1;
      let results = first?.data ?? [];
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            fetchStylePropertiesList({
              category,
              companyId,
              page: i + 2,
              limit: LIMIT,
            }),
          ),
        );
        results = [...results, ...rest.flatMap(r => r?.data ?? [])];
      }
      return results.map(p => ({ key: p.id, label: p.value }));
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId,
  });
}

function useStylePropertyCategory(
  category: StylePropertyCategory,
  companyId: string,
) {
  return useQuery(stylePropertyCategoryQueryOptions(category, companyId));
}

export function useStyleFilterOptions() {
  const companyId = useSessionStore(s => s.user?.companyId ?? '');

  const styleTypes = useStyleTypeOptions();
  const networkCompanies = useNetworkCompanyOptions();
  const brands = useStylePropertyCategory('brands', companyId);
  const departments = useStylePropertyCategory('departments', companyId);
  const seasons = useStylePropertyCategory('seasons', companyId);
  const collections = useStylePropertyCategory('collections', companyId);

  return {
    styleTypeOptions: styleTypes.data ?? [],
    companyOptions: networkCompanies.data ?? [],
    brandOptions: brands.data ?? [],
    departmentOptions: departments.data ?? [],
    seasonOptions: seasons.data ?? [],
    collectionOptions: collections.data ?? [],
  };
}
